
import React, { useState, useEffect, useRef } from 'react';

interface WitnessData {
  votes: string;
  missed_blocks: number;
  last_confirmed_block_num: number;
  signing_key: string;
  url: string;
  running_version: string;
  hardfork_version_vote: string;
  last_sbd_exchange_update: string;
  sbd_exchange_rate?: {
    base: string;
    quote: string;
  };
  props?: {
    account_creation_fee: string;
    maximum_block_size: number;
    hbd_interest_rate: number;
  };
}

const WitnessSection: React.FC = () => {
  const [witness, setWitness] = useState<WitnessData | null>(null);
  const [voterCount, setVoterCount] = useState<number | null>(null);
  const [hasKeychain, setHasKeychain] = useState<boolean>(false);
  const [logs, setLogs] = useState<{ id: number; timestamp: string; type: string; message: string }[]>([]);
  const [currentBlock, setCurrentBlock] = useState<number>(0);
  const logIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const addLog = (type: string, message: string) => {
    const newLog = {
      id: logIdRef.current++,
      timestamp: new Date().toLocaleTimeString([], { hour12: false }),
      type,
      message
    };
    setLogs(prev => [...prev.slice(-12), newLog]);
  };

  // Check for Keychain on mount
  useEffect(() => {
    const checkKeychain = () => {
      // Fix: Cast window to any to access injected hive_keychain property
      if ((window as any).hive_keychain) {
        setHasKeychain(true);
        addLog('SYSTEM', 'Hive Keychain detected. Secure voting enabled.');
      }
    };
    checkKeychain();
    // Re-check after a short delay in case of late injection
    setTimeout(checkKeychain, 1000);
  }, []);

  const handleKeychainVote = () => {
    // Fix: Cast window to any to access injected hive_keychain property
    if (!(window as any).hive_keychain) return;
    
    addLog('REQUEST', 'Initiating Keychain witness vote broadcast...');
    
    // Fix: Cast window to any to access injected hive_keychain property
    (window as any).hive_keychain.requestWitnessVote(
      null, 
      'louis.witness', 
      true, 
      (response: any) => {
        if (response.success) {
          addLog('SIGN', 'Witness vote confirmed via Keychain. Thank you!');
        } else {
          addLog('ERROR', `Vote rejected: ${response.message || 'User cancelled'}`);
        }
      }
    );
  };

  // Fetch Witness Profile + Voter Stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Witness Info (Condenser API)
        const resWitness = await fetch('https://api.hive.blog', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'condenser_api.get_witness_by_account',
            params: ['louis.witness'],
            id: 1
          })
        });
        const dataWitness = await resWitness.json();
        if (dataWitness.result) {
          setWitness(dataWitness.result);
          addLog('SYSTEM', `Registry data retrieved for @louis.witness`);
        }

        // Voter Statistics (HAFBE API)
        const resVoters = await fetch('https://api.syncad.com/hafbe-api/witnesses/louis.witness/voters?page=1&page-size=1&sort=vests&direction=desc');
        const dataVoters = await resVoters.json();
        if (dataVoters.total_votes) {
          setVoterCount(dataVoters.total_votes);
          addLog('NETWORK', `${dataVoters.total_votes} active endorsements verified`);
        }

      } catch (err) {
        console.error("Fetch failure", err);
        addLog('ERROR', 'Connection to protocol layer timed out');
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Block Polling
  useEffect(() => {
    const pollBlocks = async () => {
      try {
        const response = await fetch('https://api.hive.blog', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'condenser_api.get_dynamic_global_properties',
            params: [],
            id: 1
          })
        });
        const data = await response.json();
        if (data.result) {
          const newBlock = data.result.head_block_number;
          if (newBlock !== currentBlock) {
            setCurrentBlock(newBlock);
            if (data.result.current_witness === 'louis.witness') {
              addLog('SIGN', `PRODUCED BLOCK #${newBlock} [SUCCESS]`);
            } else {
              addLog('P2P', `Verified block #${newBlock} from @${data.result.current_witness}`);
            }
          }
        }
      } catch (err) {
        addLog('ERROR', 'Node sync delayed. Retrying handshake...');
      }
    };

    const interval = setInterval(pollBlocks, 3000);
    return () => clearInterval(interval);
  }, [currentBlock]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  const formatVotes = (votes: string) => {
    if (!votes) return '---';
    const power = (parseFloat(votes) / 1e12).toFixed(2);
    return `${power}M HP`;
  };

  const getTimeAgo = (timestamp: string) => {
    if (!timestamp || timestamp === '1970-01-01T00:00:00') return '---';
    const date = new Date(timestamp + 'Z');
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (isNaN(seconds) || seconds < 0) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  return (
    <section id="witness" className="max-w-[1600px] mx-auto border-x border-b border-zinc-800 relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[500px]">
        
        {/* Left: Identity & Metrics */}
        <div className="lg:col-span-5 p-12 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-zinc-800 bg-zinc-950/40">
          <div className="font-mono text-xs text-red-600 uppercase tracking-[0.4em] mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_10px_#22c55e]"></span>
            Witness_Live: @louis.witness
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-white tracking-tighter uppercase mb-8 leading-none">
            Active<br /><span className="text-red-600">Guardian</span>
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            <div className="p-6 bg-zinc-900/40 border border-zinc-800">
              <div className="font-mono text-[9px] text-zinc-600 uppercase mb-2 tracking-widest">Global_Approval</div>
              <div className="text-2xl font-black font-mono text-white">
                {witness ? formatVotes(witness.votes) : '---'}
              </div>
            </div>
            <div className="p-6 bg-zinc-900/40 border border-zinc-800">
              <div className="font-mono text-[9px] text-zinc-600 uppercase mb-2 tracking-widest">Unique_Voters</div>
              <div className="text-2xl font-black font-mono text-white">
                {voterCount ? voterCount.toLocaleString() : '---'}
              </div>
            </div>
          </div>

          {/* Keychain CTA */}
          {hasKeychain && (
            <button 
              onClick={handleKeychainVote}
              className="w-full mb-8 py-5 bg-white text-black font-mono text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-red-600 hover:text-white transition-all border-none cursor-pointer flex items-center justify-center gap-4 group shadow-xl active:scale-[0.98]"
            >
              <i className="fa-solid fa-key group-hover:rotate-45 transition-transform"></i>
              Vote_With_Keychain
            </button>
          )}
          
          <div className="flex flex-wrap gap-6">
            <a href="https://peakd.com/@louis.witness" target="_blank" className="inline-flex items-center gap-3 text-white font-mono text-[10px] uppercase tracking-widest hover:text-red-600 transition-all group no-underline">
              <i className="fa-solid fa-square-rss group-hover:rotate-12 transition-transform"></i> 
              <span className="border-b border-zinc-800 group-hover:border-red-600 pb-1">Read_Proposal</span>
            </a>
            <a href="https://vote.hive.uno/@louis.witness" target="_blank" className="inline-flex items-center gap-3 text-white font-mono text-[10px] uppercase tracking-widest hover:text-red-600 transition-all group no-underline">
              <i className="fa-solid fa-check-to-slot group-hover:scale-110 transition-transform"></i> 
              <span className="border-b border-zinc-800 group-hover:border-red-600 pb-1">Manual_Vote</span>
            </a>
          </div>
        </div>

        {/* Right: Technical Stats & Terminal */}
        <div className="lg:col-span-7 flex flex-col">
          <div className="grid grid-cols-1 md:grid-cols-2 flex-1">
            <div className="p-8 border-b md:border-r border-zinc-800 bg-zinc-900/10">
              <div className="font-mono text-[10px] text-zinc-600 uppercase mb-6 tracking-widest flex items-center justify-between">
                Network_Parameters
                <i className="fa-solid fa-gears text-zinc-800"></i>
              </div>
              <ul className="space-y-4 font-mono text-[11px] text-zinc-400">
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">ENGINE_VER</span>
                  <span className="text-white font-bold">{witness?.running_version || '---'}</span>
                </li>
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">MAX_BLOCK_SIZE</span>
                  <span className="text-white">{witness?.props ? (witness.props.maximum_block_size / 1024).toFixed(0) : '---'} KB</span>
                </li>
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">HBD_INTEREST</span>
                  <span className="text-white">{witness?.props ? (witness.props.hbd_interest_rate / 100).toFixed(1) : '---'}%</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-500">HF_VOTE_STATE</span>
                  <span className="text-white">{witness?.hardfork_version_vote || '---'}</span>
                </li>
              </ul>
            </div>

            <div className="p-8 border-b border-zinc-800 bg-zinc-900/10">
              <div className="font-mono text-[10px] text-zinc-600 uppercase mb-6 tracking-widest flex items-center justify-between">
                Market_Protocol
                <i className="fa-solid fa-chart-line text-zinc-800"></i>
              </div>
              <ul className="space-y-4 font-mono text-[11px] text-zinc-400">
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">HIVE_PRICE_FEED</span>
                  <span className="text-red-500 font-bold">{witness?.sbd_exchange_rate?.base || '---'}</span>
                </li>
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">FEED_UPDATED</span>
                  <span className="text-white">{getTimeAgo(witness?.last_sbd_exchange_update || '')}</span>
                </li>
                <li className="flex justify-between border-b border-zinc-800/30 pb-2">
                  <span className="text-zinc-500">ACCOUNT_FEE</span>
                  <span className="text-white">{witness?.props?.account_creation_fee || '---'}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-500">LAST_SIG_BLOCK</span>
                  {witness ? (
                    <a href={`https://hivehub.dev/b/${witness.last_confirmed_block_num}`} target="_blank" rel="noopener noreferrer" className="text-white font-bold hover:text-red-500 transition-colors no-underline">
                      {witness.last_confirmed_block_num.toLocaleString()}
                    </a>
                  ) : (
                    <span className="text-white">---</span>
                  )}
                </li>
              </ul>
            </div>
          </div>

          {/* Terminal Console */}
          <div className="p-0 bg-[#020202] relative overflow-hidden flex-1 min-h-[350px] flex flex-col border-t border-zinc-800">
            <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between bg-zinc-950">
              <div className="font-mono text-[9px] text-zinc-500 uppercase tracking-widest flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-600"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-800"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-zinc-800"></div>
                </div>
                hived-v{witness?.running_version || 'monitor'}
              </div>
              <div className="font-mono text-[9px] text-zinc-700">SIG_KEY: {witness?.signing_key ? witness.signing_key.substring(0, 8) + '...' : 'LOCKED'}</div>
            </div>

            <div ref={scrollRef} className="p-6 font-mono text-[10px] md:text-[11px] leading-relaxed overflow-y-auto max-h-[300px] scrollbar-hide">
              {logs.length === 0 && <div className="text-zinc-700">Initializating blockchain handshake...</div>}
              {logs.map((log) => (
                <div key={log.id} className="mb-1 flex gap-4 animate-in fade-in slide-in-from-left-1 duration-200">
                  <span className="text-zinc-700 shrink-0 opacity-50">[{log.timestamp}]</span>
                  <span className={`shrink-0 w-12 font-bold ${
                    log.type === 'SIGN' ? 'text-red-600' : 
                    log.type === 'NETWORK' ? 'text-blue-500' : 
                    log.type === 'REQUEST' ? 'text-yellow-500' : 'text-zinc-500'
                  }`}>
                    {log.type}
                  </span>
                  <span className={log.type === 'SIGN' ? 'text-white font-bold' : 'text-zinc-400'}>
                    {log.message}
                  </span>
                </div>
              ))}
              <div className="mt-2 flex items-center gap-2">
                <span className="text-zinc-800">[{new Date().toLocaleTimeString([], { hour12: false })}]</span>
                <span className="text-red-600 animate-pulse">_</span>
              </div>
            </div>
            
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
              <i className="fa-brands fa-hive text-[300px]"></i>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WitnessSection;
